import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { AttributeKey } from '@yoimo/interfaces';
import { isEmpty } from 'lodash';
import { lastValueFrom } from 'rxjs';
import * as XLSX from 'xlsx';
import { BulkImportService } from './bulk-import.service';

@Injectable({
    providedIn: 'root'
  })
export class DownloadCSVService {

    constructor(
        private readonly functions: Functions,
        private readonly bulkService: BulkImportService,
        private readonly http: HttpClient
    ){}
    downloadFile(data: Record<string, any>[], filename?: string, headerList?: any) {
        if(!filename){
            filename= 'data';
        }
        const csvData = this.ConvertToCSV(data, headerList);
        this.downloadCSVData(csvData, filename);
    }

    downloadCSVData(csvData: string, filename = 'data') {
        const blob = new Blob(['\ufeff' + csvData], { type: 'text/csv;charset=utf-8;' });
        const dwldLink = document.createElement('a');
        const url = URL.createObjectURL(blob);
        const isSafariBrowser = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
        if (isSafariBrowser) {
            dwldLink.setAttribute('target', '_blank');
        }
        dwldLink.setAttribute('href', url);
        dwldLink.setAttribute('download', filename + '.csv');
        dwldLink.style.visibility = 'hidden';
        document.body.appendChild(dwldLink);
        dwldLink.click();
        dwldLink.remove();
    }

    async downLoadReferenceSheet(isVideos?: boolean) {
        const attributesExcelData = this.prepareAttributesData();
        const reservedInputExcelData = isVideos ? [] : this.prepareReservedInputData();

        const [timeZonesCsvData, countryCodeCsvData] = await this.fetchCsvData();

        const workbook = this.createWorkbook(
            timeZonesCsvData,
            countryCodeCsvData,
            attributesExcelData,
            reservedInputExcelData,
            isVideos
        );

        this.downloadWorkbook(workbook, 'reference_data_sheet.xlsx');
    }

    private prepareAttributesData(): Record<string, string>[] {
        const attributesExcelData: Record<string, string>[] = [];
        const currentAttributeKeys = Object.keys(this.bulkService.attributeMappings)
            .sort((a, b) => a.localeCompare(b)) as AttributeKey[];
        
        for (const item of currentAttributeKeys) {
            const currentAttribute = this.bulkService.attributeMappings[item];
            const attributeHeader = `attribute_${item[item.length - 1]}(${currentAttribute?.label})`;
            
            if (currentAttribute?.values) {
                for (const [index, value] of currentAttribute.values.entries()) {
                    if (!attributesExcelData[index]) {
                        attributesExcelData.push({});
                    }
                    attributesExcelData[index][attributeHeader] = value.name;
                }
            }
        }
        
        return attributesExcelData;
    }

    private prepareReservedInputData(): Record<string, string>[] {
        const reservedInputExcelData: Record<string, string>[] = [];
        
        for (const [index, item] of this.bulkService.reservedInput.entries()) {
            if (!reservedInputExcelData[index]) {
                reservedInputExcelData.push({});
            }
            reservedInputExcelData[index]['Reserved input ID'] = item.id;
        }
        
        return reservedInputExcelData;
    }

    private async fetchCsvData(): Promise<[string, string]> {
        const timeZonesCsvData = await lastValueFrom(
            this.http.get('../../assets/timezones.csv', { responseType: 'text' })
        );
        const countryCodeCsvData = await lastValueFrom(
            this.http.get('../../assets/country_codes.csv', { responseType: 'text' })
        );
        
        return [timeZonesCsvData, countryCodeCsvData];
    }

    private createWorkbook(
        timeZonesCsvData: string,
        countryCodeCsvData: string,
        attributesExcelData: Record<string, string>[],
        reservedInputExcelData: Record<string, string>[],
        isVideos?: boolean
    ): XLSX.WorkBook {
        const workbook = XLSX.utils.book_new();
        
        const timeZoneSheet = this.createCsvSheet(timeZonesCsvData);
        const countryCodeSheet = this.createCsvSheet(countryCodeCsvData);
        const attributeSheet = this.createCsvFromJson(
            attributesExcelData,
            'Attributes',
            'Attributes are not configured for this channel'
        );
        
        XLSX.utils.book_append_sheet(workbook, timeZoneSheet, 'Time zone');
        XLSX.utils.book_append_sheet(workbook, countryCodeSheet, 'Country code');
        XLSX.utils.book_append_sheet(workbook, attributeSheet, 'Attributes');
        
        if (!isVideos) {
            const reservedInputSheet = this.createCsvFromJson(
                reservedInputExcelData,
                'Reserved input ID',
                'No reserved input details available'
            );
            XLSX.utils.book_append_sheet(workbook, reservedInputSheet, "Reserved input ID");
        }
        
        return workbook;
    }

    private createCsvSheet(csvData: string): XLSX.WorkSheet {
        return XLSX.utils.aoa_to_sheet(
            csvData.split('\n').map((row) => row.split(','))
        );
    }

    private createCsvFromJson(
        excelData: Record<string, string>[],
        fieldName: string,
        emptyMessage: string
        ): XLSX.WorkSheet {
        const hasData = excelData.length > 0 && !isEmpty(excelData[0]);

        const data = hasData
            ? excelData
            : [{ [fieldName]: emptyMessage }];

        return XLSX.utils.json_to_sheet(data);
    }

    private downloadWorkbook(workbook: XLSX.WorkBook, filename: string): void {
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = globalThis.URL.createObjectURL(blob);
        const link = document.createElement('a');
        
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        globalThis.URL.revokeObjectURL(url);
    }

    ConvertToCSV(objArray: Record<string, string | number | undefined | number[]>[], headerList: string[], noAttributes = true) {
        const array = typeof objArray == 'object' ? objArray : JSON.parse(objArray);
        
        if (array.length > 1 && noAttributes) {
            return this.convertWithSerialNumber(array, headerList);
        } else {
            return this.convertWithoutSerialNumber(array, headerList);
        }
    }

    private convertWithSerialNumber(array: Record<string, any>[], headerList: string[]): string {
        let str = '';
        
        const headerRow = 'S. No,' + headerList.join(',');
        str += headerRow + '\r\n';
        
        for (const [index, row] of array.entries()) {
            const serialNumber = index + 1;
            const values = headerList.map(header => `"${row[header] || ''}"`);
            const line = serialNumber + ',' + values.join(',');
            str += line + '\r\n';
        }
        
        return str;
    }

    private convertWithoutSerialNumber(array: Record<string, any>[], headerList: string[]): string {
        let str = '';
        
        const headerRow = headerList.join(',');
        str += headerRow + '\r\n';
        
        for (const row of array) {
            const values = headerList.map(header => `"${row[header] || ''}"`);
            const line = values.join(',');
            str += line + '\r\n';
        }
        
        return str;
    }
    
    async sendEmail(clubDocId: string, exportType: string, startTime: string, endTime: string){
        const response =  await httpsCallable(this.functions, 'club-export')({
        clubDocId: clubDocId,
        endTime: endTime,
        exportType: exportType,
        startTime: startTime,
       });
       return response.data;
    }
}
